// SimpleLine — port of the prototype's SVG sparkline (v5 HTML lines
// 600-617). Pure SVG, no chart library. preserveAspectRatio="none" lets
// the line stretch to whatever container width it lands in.
//
// Empty-state: if no points are supplied (or every value is 0) we render
// a centred placeholder at the same height — a flat line at the bottom
// would look like real data and be misleading.

interface SimpleLineProps {
  data: Array<{ value: number }>
  height?: number
  emptyLabel?: string
}

export function SimpleLine({ data, height = 200, emptyLabel = 'No sales data yet' }: SimpleLineProps) {
  const allZero = data.length === 0 || data.every((d) => d.value === 0)
  if (allZero) {
    return (
      <div
        className="flex items-center justify-center text-sm text-ink-soft italic"
        style={{ height }}
      >
        {emptyLabel}
      </div>
    )
  }

  // Single-point edge case — render a single dot rather than dividing by 0
  // when computing x positions.
  if (data.length === 1) {
    const r = 1.2
    return (
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ width: '100%', height, display: 'block' }}>
        <circle cx="50" cy="50" r={r} fill="var(--green, #0a4a3a)" />
      </svg>
    )
  }

  const max = Math.max(...data.map((d) => d.value))
  const w = 100
  // y = 100 - (value/max) * 90 maps max → y=10 (top with 10px padding) and 0 → y=100 (bottom).
  const points = data
    .map((d, i) => `${(i / (data.length - 1)) * w},${100 - (d.value / max) * 90}`)
    .join(' ')
  const fillPoints = `0,100 ${points} ${w},100`

  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ width: '100%', height, display: 'block' }}>
      <defs>
        <linearGradient id="nymr-line-grad" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#0a4a3a" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#0a4a3a" stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={fillPoints} fill="url(#nymr-line-grad)" />
      <polyline
        points={points}
        fill="none"
        stroke="#0a4a3a"
        strokeWidth="0.6"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  )
}
